// src/components/VideoPlayer.jsx
import { useState, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useVideoLoader } from "../hooks/useVideoLoader";
import { bumpVideoView, shouldBumpLocal } from "../services/views";

/** --- helpers --- */
const ensureAbsolute = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `https://new-nightlife.jp${url.startsWith("/") ? "" : "/"}${url}`;
};

const toIso8601Duration = (d) => {
  if (typeof d === "string" && /^PT/i.test(d)) return d;
  const secNum = Number.isFinite(d) ? Math.max(0, Math.floor(d)) : 0;
  const h = Math.floor(secNum / 3600);
  const m = Math.floor((secNum % 3600) / 60);
  const s = secNum % 60;
  return `PT${h ? `${h}H` : ""}${m ? `${m}M` : ""}${(s || (!h && !m)) ? `${s}S` : ""}`;
};

const safeKeywords = (tags = []) => tags.filter(Boolean).join(", ");
const clamp = (str = "", n = 100) => (str.length > n ? `${str.slice(0, n - 1)}…` : str);

const VideoPlayer = ({
  // 親から渡される Firestore の docId（ビュー加算に使用）
  videoId,
  videoUrl,
  thumbnailUrl,
  onNext,
  onPrev,
  preloadNextUrl,
  title = "夜職って実際どう？働く女の子のリアルな声",
  description = "未経験から始めた女の子のインタビュー動画",
  uploadedAt = new Date().toISOString(),
  duration = "PT1M30S",
  tags = [],
  manageHead = false,        // ページ側で <Helmet> を管理していない場合だけ true
  videoRef: externalRef,     // ← 親から受け取る ref（任意）
}) => {
  const internalRef = useRef(null);
  const videoRef = externalRef || internalRef;

  const [isPlaying, setIsPlaying] = useState(false);
  const [bumped, setBumped] = useState(false); // 再生一度きりの +1 フラグ
  const [transitioning, setTransitioning] = useState(false);

  const {
    isLoading,
    hasError,
    canRetry,
    isLoaded,
    isPreloading,
    retry,
  } = useVideoLoader(videoUrl, { preloadNextUrl });

  const handlePlayClick = () => {
    if (!isLoaded || !videoRef.current) return;
    try {
      videoRef.current.play();
      setIsPlaying(true);

      // 再生開始時に 1 回だけビュー加算（ローカル二重防止つき）
      if (!bumped && videoId && shouldBumpLocal(videoId)) {
        setBumped(true);
        bumpVideoView(videoId).catch((e) => {
          console.warn("bumpVideoView failed:", e?.message || e);
        });
      }
    } catch {
      // 再生不可でも UI は継続
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handlePlayClick();
    }
  };

  const handleEnded = () => {
    if (typeof onNext === "function") {
      setTransitioning(true);
      setTimeout(() => {
        setTransitioning(false);
        onNext();
      }, 600);
    }
  };

  // --- SEO: VideoObject JSON-LD（必要時だけ出す） ---
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description,
    thumbnailUrl: [ensureAbsolute(thumbnailUrl || "/images/video/placeholder-640x360.jpg")],
    uploadDate: uploadedAt,
    duration: toIso8601Duration(duration),
    contentUrl: ensureAbsolute(videoUrl),
    embedUrl: "https://new-nightlife.jp/#video",
    publisher: {
      "@type": "Organization",
      name: "New-Nightlife",
      url: "https://new-nightlife.jp/",
      logo: {
        "@type": "ImageObject",
        url: "https://new-nightlife.jp/apple-touch-icon.png",
      },
    },
    keywords: safeKeywords(tags),
  }), [title, description, thumbnailUrl, uploadedAt, duration, videoUrl, tags]);

  const absThumb = ensureAbsolute(thumbnailUrl || "/images/video/placeholder-640x360.jpg");
  const videoDomId = `video-player-${videoId || "x"}`; // DOM 用のID（重複防止）

  return (
    <div style={styles.container}>
      {manageHead && (
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={clamp(description, 120)} />
          {tags.length > 0 && <meta name="keywords" content={safeKeywords(tags)} />}
          <link rel="canonical" href="https://new-nightlife.jp/" />

          {/* サムネ優先読込 */}
          <link rel="preload" as="image" href={absThumb} />
          {/* 次動画の軽い先読み（同一オリジン想定） */}
          {preloadNextUrl && <link rel="prefetch" as="video" href={preloadNextUrl} />}

          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes pop { from { transform: scale(0.96); } to { transform: scale(1.04); } }
            button:focus-visible { outline: 2px solid #ff80ab; outline-offset: 2px; }
          `}</style>
        </Helmet>
      )}

      <h2 style={styles.title}>{title}</h2>

      <div style={styles.slider} aria-label="動画スライダー">
        <button
          style={styles.arrowButton}
          onClick={onPrev}
          aria-label="前の動画へ"
          type="button"
        >
          ＜
        </button>

        <div
          style={styles.videoWrapper}
          aria-busy={isLoading || isPreloading ? "true" : "false"}
        >
          <div
            role="button"
            tabIndex={0}
            aria-controls={videoDomId}
            aria-label={isLoaded ? "動画を再生" : "動画読み込み中"}
            onKeyDown={handleKey}
            style={{
              ...styles.thumbnailContainer,
              opacity: isPlaying ? 0 : 1,
              zIndex: isPlaying ? 0 : 2,
              transition: "opacity 0.5s ease-in-out",
            }}
            onClick={handlePlayClick}
          >
            <img
              src={thumbnailUrl || "https://placehold.co/640x360?text=No+Thumbnail"}
              alt={`${title} のサムネイル画像`}
              style={styles.thumbnail}
              width={640}
              height={360}
              loading="eager"
              decoding="async"
            />
            {(isLoading || isPreloading) && (
              <div style={styles.spinnerOverlay} role="status" aria-live="polite">
                <div style={styles.spinner} />
                <div style={styles.loadingText}>読み込み中...</div>
              </div>
            )}
            {!isPlaying && isLoaded && !hasError && <div style={styles.playButton}>▶</div>}
            {hasError && (
              <div style={styles.errorOverlay}>
                <p>❌ この動画は壊れている可能性があります</p>
                {canRetry && (
                  <button onClick={retry} style={styles.cancelButton} type="button" aria-label="動画を再読み込み">
                    再読み込み
                  </button>
                )}
              </div>
            )}
          </div>

          <video
            id={videoDomId}
            key={videoUrl}
            ref={videoRef}
            src={videoUrl}
            style={{
              ...styles.video,
              opacity: transitioning ? 0 : isPlaying ? 1 : 0,
              filter: hasError ? "grayscale(100%) brightness(70%)" : "none",
              transition: "opacity 0.5s ease-in-out",
            }}
            poster={thumbnailUrl || undefined}
            preload="metadata"
            controls
            controlsList="nodownload"
            muted
            playsInline
            // iOS Safari 対策（念のため）
            webkit-playsinline="true"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleEnded}
          />
        </div>

        <button
          style={styles.arrowButton}
          onClick={onNext}
          aria-label="次の動画へ"
          type="button"
        >
          ＞
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "16px",
    textAlign: "center",
    fontFamily:
      "'M PLUS Rounded 1c', 'Kosugi Maru', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  title: {
    fontSize: "20px",
    fontWeight: 800,
    color: "#e91e63",
    margin: "16px 0 6px",
    textShadow: "0 1px 1px rgba(0,0,0,0.1)",
  },
  slider: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },
  arrowButton: {
    background: "linear-gradient(135deg, #ffd1dc, #ff80ab)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "48px",
    height: "48px",
    fontSize: "20px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    cursor: "pointer",
    transition: "transform 0.2s ease",
  },
  videoWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: "90vw",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
  },
  video: {
    width: "100%",
    borderRadius: "16px",
    objectFit: "cover",
    backgroundColor: "#000",
  },
  thumbnailContainer: {
    position: "absolute",
    inset: 0,
    backgroundColor: "#000",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  thumbnail: {
    width: "100%",
    height: "auto",
    objectFit: "cover",
  },
  spinnerOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0.05))",
    backdropFilter: "blur(4px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  loadingText: {
    color: "#fff",
    fontSize: "12px",
    marginTop: "4px",
  },
  playButton: {
    position: "absolute",
    fontSize: "40px",
    color: "#fff",
    background: "linear-gradient(135deg, #ff4081, #ff80ab)",
    borderRadius: "50%",
    padding: "16px 20px",
    boxShadow: "0 6px 18px rgba(255,105,135,0.4)",
    animation: "pop 0.8s ease-in-out infinite alternate",
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "4px solid #fff",
    borderTop: "4px solid #ff4081",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  errorOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(255, 240, 245, 0.95)",
    color: "#D81B60",
    fontWeight: 600,
    fontSize: "14px",
    textAlign: "center",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    zIndex: 3,
  },
  cancelButton: {
    backgroundColor: "#fff",
    color: "#ff4081",
    padding: "10px 20px",
    border: "none",
    borderRadius: "20px",
    fontSize: "15px",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  },
};

export default VideoPlayer;
